
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import ImageBase from '@/components/atoms/ImageBase.vue'
import TitlePage from '@/components/atoms/TitlePage.vue'
import ButtonStrongSquare from '@/components/molecules/ButtonStrongSquare.vue'
import ButtonSubmitSquare from '@/components/molecules/ButtonSubmitSquare.vue'
import ButtonOptionalSquare from '@/components/molecules/ButtonOptionalSquare.vue'
import ClickTooltip from '@/components/molecules/ClickTooltip.vue'
import QuestionInputArea from '@/components/organisms/QuestionInputArea.vue'
import ButtonInput from '@/components/organisms/study/ButtonInput.vue'
import MemoArea from '@/components/organisms/study/MemoArea.vue'
import ModalStylePageLayout from '@/components/organisms/layout/ModalStylePageLayout.vue'
import ModalCorrectInputText from '@/views/modal/study/ModalCorrectInputText.vue'
import ModalCorrectInputNumericlal from '@/views/modal/study/ModalCorrectInputNumericlal.vue'
import ModalCorrectInputJapanese from '@/views/modal/study/ModalCorrectInputJapanese.vue'
import ModalCorrectInputEnglish from '@/views/modal/study/ModalCorrectInputEnglish.vue'
import ModalCorrectSingleChoice from '@/views/modal/study/ModalCorrectSingleChoice.vue'
import { StudyModel } from '@/models/StudyModel'
import { CurriculumUnit } from '@/models/CurriculumUnit'

/**
 * 小問演習画面のテンプレート
 */
@Component({
  components: {
    ImageBase,
    TitlePage,
    ButtonStrongSquare,
    ButtonSubmitSquare,
    ButtonOptionalSquare,
    ClickTooltip,
    QuestionInputArea,
    ButtonInput,
    MemoArea,
    ModalStylePageLayout,
    ModalCorrectInputText,
    ModalCorrectInputNumericlal,
    ModalCorrectInputJapanese,
    ModalCorrectInputEnglish,
    ModalCorrectSingleChoice,
  },
})
export default class LearningProblem extends Vue {
  /** テキスト入力モーダル */
  @Ref()
  modalCorrectInputText!: ModalCorrectInputText

  /** 数式入力モーダル */
  @Ref()
  modalCorrectInputNumericlal!: ModalCorrectInputNumericlal

  /** 日本語入力モーダル */
  @Ref()
  modalCorrectInputJapanese!: ModalCorrectInputJapanese

  /** 英語入力モーダル */
  @Ref()
  modalCorrectInputEnglish!: ModalCorrectInputEnglish

  /** 択一式入力モーダル */
  @Ref()
  modalCorrectSingleChoice!: ModalCorrectSingleChoice

  /** メモエリア */
  @Ref()
  memoArea!: MemoArea

  /** 単元情報 */
  @Prop()
  curriculumUnit!: CurriculumUnit

  /** 小問情報 */
  @Prop()
  problem!: StudyModel.Problem

  /** 送信ボタンクリック時イベント */
  @Prop()
  eventSubmit!: () => Promise<void>

  /** 戻るボタンクリック時イベント */
  @Prop()
  eventReturn!: () => Promise<void>

  /** トップページボタンクリック時イベント */
  @Prop()
  eventTopPage!: () => Promise<void>

  /** 戻り先画面情報の有無 */
  @Prop({ default: true })
  hasPrevPage!: boolean

  /** メモエリアの表示状態 */
  private isShowMemoArea = false

  /**
   * メモ機能の有効/無効
   *
   * @returns 有効状態
   */
  private get enableMemo(): boolean {
    return !!process.env.VUE_APP_ENABLE_MEMO
  }

  /**
   * メモエリアの表示切り替え
   */
  private toggleMemoArea(): void {
    this.isShowMemoArea = !this.isShowMemoArea
  }

  /**
   * メモエリアの非表示化
   */
  private hideMemoArea(): void {
    this.isShowMemoArea = false
  }

  /**
   * テキスト入力時のイベント
   *
   * @param question 設問情報
   * @param index 入力欄のインデックス
   * @param afterFunction 入力完了後の処理
   */
  private inputText(question: StudyModel.Question, index: number, afterFunction: StudyModel.AfterInputFunction): void {
    this.modalCorrectInputText.showModal(question, index, afterFunction)
  }

  /**
   * 数式入力時のイベント
   *
   * @param question 設問情報
   * @param index 入力欄のインデックス
   * @param afterFunction 入力完了後の処理
   */
  private inputNumericalFormula(
    question: StudyModel.Question,
    index: number,
    afterFunction: StudyModel.AfterInputNumericalFormulaFunction
  ): void {
    this.modalCorrectInputNumericlal.showModal(question, index, afterFunction)
  }

  /**
   * 日本語手書き入力時のイベント
   *
   * @param question 設問情報
   * @param index 入力欄のインデックス
   * @param afterFunction 入力完了後の処理
   */
  private inputJapanese(
    question: StudyModel.Question,
    index: number,
    afterFunction: StudyModel.AfterInputJapaneseFunction
  ): void {
    this.modalCorrectInputJapanese.showModal(question, index, afterFunction)
  }

  /**
   * 英語手書き入力時のイベント
   *
   * @param question 設問情報
   * @param index 入力欄のインデックス
   * @param afterFunction 入力完了後の処理
   */
  private inputEnglish(
    question: StudyModel.Question,
    index: number,
    afterFunction: StudyModel.AfterInputEnglishFunction
  ): void {
    this.modalCorrectInputEnglish.showModal(question, index, afterFunction)
  }

  /**
   * 択一式入力時のイベント
   *
   * @param question 設問情報
   * @param afterFunction 入力完了後の処理
   */
  private inputSingleChoice(
    question: StudyModel.Question,
    afterFunction: StudyModel.AfterInputSingleChoiceFunction
  ): void {
    this.modalCorrectSingleChoice.showModal(question, afterFunction)
  }
}
